// PATH: src/pages/Biblia.jsx
// VERSION: 4.2.1 — Dropdowns con máximo ~10 ítems visibles + scroll (sin cambios de lógica)

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Container,
  Heading,
  VStack,
  HStack,
  Button,
  Text,
  SimpleGrid,
  useToast,
  Divider,
  Badge,
  IconButton,
  Skeleton,
  Alert,
  AlertIcon,
  Tooltip,
  Flex,
  Menu,
  MenuButton,
  MenuList,
  MenuOptionGroup,
  MenuItemOption,
  MenuDivider,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  CopyIcon,
  RepeatIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDownIcon,
} from '@chakra-ui/icons';
import { FiShare2 } from 'react-icons/fi';
import booksES from '../data/books_es.json';
import ShareCardModal from '../components/common/ShareCardModal';

/* ────────────────────────────────────────────────────────────────────────────
   Config API: Bible SuperSearch (CORS enabled, sin clave)
   Doc: https://api.biblesupersearch.com
   ──────────────────────────────────────────────────────────────────────────── */
const BSS_API = 'https://api.biblesupersearch.com/api';

/* Versiones disponibles */
const VERSIONS = [
  { value: 'rv_1909', label: 'Reina-Valera 1909' },
  { value: 'rv_1909_strongs', label: 'RV1909 + Strong’s' },
  { value: 'rv_1858', label: 'Reina-Valera 1858 (NT)' },
  { value: 'sagradas', label: 'Sagradas Escrituras 1569' },
  { value: 'rvg', label: 'Reina-Valera Gómez 2010' },
];

/* Capítulos por libro (slug del JSON de libros) */
const CHAPTERS_BY_SLUG = {
  genesis: 50, exodo: 40, levitico: 27, numeros: 36, deuteronomio: 34,
  josue: 24, jueces: 21, rut: 4, '1-samuel': 31, '2-samuel': 24,
  '1-reyes': 22, '2-reyes': 25, '1-cronicas': 29, '2-cronicas': 36,
  esdras: 10, nehemias: 13, ester: 10, job: 42, salmos: 150,
  proverbios: 31, eclesiastes: 12, cantares: 8, isaias: 66, jeremias: 52,
  lamentaciones: 5, ezequiel: 48, daniel: 12, oseas: 14, joel: 3, amos: 9,
  abdias: 1, jonas: 4, miqueas: 7, nahum: 3, habacuc: 3, sofonias: 3,
  hageo: 2, zacarias: 14, malaquias: 4,
  mateo: 28, marcos: 16, lucas: 24, juan: 21, hechos: 28, romanos: 16,
  '1-corintios': 16, '2-corintios': 13, galatas: 6, efesios: 6,
  filipenses: 4, colosenses: 4, '1-tesalonicenses': 5, '2-tesalonicenses': 3,
  '1-timoteo': 6, '2-timoteo': 4, tito: 3, filemon: 1, hebreos: 13,
  santiago: 5, '1-pedro': 5, '2-pedro': 3, '1-juan': 5, '2-juan': 1,
  '3-juan': 1, judas: 1, apocalipsis: 22,
};

/* Mapa español->inglés para referencia del API */
const ENG_BY_SLUG = {
  genesis: 'Genesis', exodo: 'Exodus', levitico: 'Leviticus', numeros: 'Numbers', deuteronomio: 'Deuteronomy',
  josue: 'Joshua', jueces: 'Judges', rut: 'Ruth', '1-samuel': '1 Samuel', '2-samuel': '2 Samuel',
  '1-reyes': '1 Kings', '2-reyes': '2 Kings', '1-cronicas': '1 Chronicles', '2-cronicas': '2 Chronicles',
  esdras: 'Ezra', nehemias: 'Nehemiah', ester: 'Esther', job: 'Job', salmos: 'Psalms',
  proverbios: 'Proverbs', eclesiastes: 'Ecclesiastes', cantares: 'Song of Solomon',
  isaias: 'Isaiah', jeremias: 'Jeremiah', lamentaciones: 'Lamentations', ezequiel: 'Ezekiel', daniel: 'Daniel',
  oseas: 'Hosea', joel: 'Joel', amos: 'Amos', abdias: 'Obadiah', jonas: 'Jonah',
  miqueas: 'Micah', nahum: 'Nahum', habacuc: 'Habakkuk', sofonias: 'Zephaniah', hageo: 'Haggai',
  zacarias: 'Zechariah', malaquias: 'Malachi',
  mateo: 'Matthew', marcos: 'Mark', lucas: 'Luke', juan: 'John', hechos: 'Acts', romanos: 'Romans',
  '1-corintios': '1 Corinthians', '2-corintios': '2 Corinthians', galatas: 'Galatians', efesios: 'Ephesians',
  filipenses: 'Philippians', colosenses: 'Colossians', '1-tesalonicenses': '1 Thessalonians', '2-tesalonicenses': '2 Thessalonians',
  '1-timoteo': '1 Timothy', '2-timoteo': '2 Timothy', tito: 'Titus', filemon: 'Philemon', hebreos: 'Hebrews',
  santiago: 'James', '1-pedro': '1 Peter', '2-pedro': '2 Peter', '1-juan': '1 John', '2-juan': '2 John',
  '3-juan': '3 John', judas: 'Jude', apocalipsis: 'Revelation',
};

/* ────────────────────────────────────────────────────────────────────────────
   Dropdown con scroll (máximo ~10 ítems visibles)
   ──────────────────────────────────────────────────────────────────────────── */
const MENU_MAX_H = '300px'; // ~10 items (depende de la altura de MenuItem)

function ScrollDropdown({
  buttonText,
  isDisabled,
  children,
}) {
  return (
    <Menu isLazy matchWidth placement="bottom-start">
      <MenuButton
        as={Button}
        rightIcon={<ChevronDownIcon />}
        w="100%"
        bg="bg.card"
        borderColor="border.default"
        borderRadius="lg"
        variant="outline"
        size="md"
        isDisabled={isDisabled}
        justifyContent="space-between"
        fontWeight="normal"
        textAlign="left"
        px={3}
      >
        {buttonText}
      </MenuButton>

      <MenuList
        maxH={MENU_MAX_H}
        overflowY="auto"
        zIndex={20}
      >
        {children}
      </MenuList>
    </Menu>
  );
}

/* Helpers de fetch/parsing */
async function bssFetch(params) {
  const url = `${BSS_API}?${new URLSearchParams(params).toString()}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const json = await res.json();
  if (Array.isArray(json?.errors) && json.errors.length) {
    throw new Error(json.errors.join(' | '));
  }
  return json;
}

/** Pide solo el capítulo para obtener el conteo real de versículos. */
async function fetchChapterCount({ bible, engBook, chapter }) {
  const data = await bssFetch({
    bible,
    reference: `${engBook} ${chapter}`,
    data_format: 'passage',
    page_all: 'true',
  });
  const first = Array.isArray(data?.results) ? data.results[0] : null;
  const count = Number(first?.verses_count || 0);
  return count;
}

/** Pide un pasaje; devuelve { verses: [{verse, text}] } */
async function fetchPassage({ bible, engBook, chapter, vFrom, vTo }) {
  const hasRange = vTo && Number(vTo) >= Number(vFrom);
  const ref = hasRange
    ? `${engBook} ${chapter}:${vFrom}-${vTo}`
    : `${engBook} ${chapter}:${vFrom}`;

  const data = await bssFetch({
    bible,
    reference: ref,
    data_format: 'passage',
    page_all: 'true',
  });

  const first = Array.isArray(data?.results) ? data.results[0] : null;
  // Estructura: results[0].verses[bible][chapter][verse] = { text, ... }
  const versesByBible = first?.verses?.[bible] || {};
  const versesOfChapter = versesByBible?.[String(chapter)] || {};
  const verseNums = Object.keys(versesOfChapter)
    .map(n => Number(n))
    .sort((a, b) => a - b);

  const verses = verseNums.map(n => ({
    verse: n,
    text: String(versesOfChapter[String(n)]?.text ?? ''),
  }));

  return { verses, refShown: ref };
}

/* ────────────────────────────────────────────────────────────────────────────
   Componente
   ──────────────────────────────────────────────────────────────────────────── */
export default function Biblia() {
  const toast = useToast();

  // Estado base (ejemplo Juan 3:16–18)
  const [version, setVersion] = useState('rv_1909');
  const [bookSlug, setBookSlug] = useState('juan');
  const [chapter, setChapter] = useState(3);
  const [verseCount, setVerseCount] = useState(0);
  const [vFrom, setVFrom] = useState(16);
  const [vTo, setVTo] = useState(18);

  const [loadingMeta, setLoadingMeta] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // { reference, version, verses:[{verse,text}] }
  const [error, setError] = useState('');
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);

  const bookName = useMemo(
    () => booksES.items.find(x => x.slug === bookSlug)?.name ?? 'Juan',
    [bookSlug]
  );

  // OT/NT para optgroups
  const idxNT = useMemo(
    () => booksES.items.findIndex(b => b.slug === 'mateo'),
    []
  );
  const booksOT = useMemo(
    () => booksES.items.slice(0, idxNT < 0 ? booksES.items.length : idxNT),
    [idxNT]
  );
  const booksNT = useMemo(
    () => booksES.items.slice(idxNT < 0 ? booksES.items.length : idxNT),
    [idxNT]
  );

  // Máximo de capítulos por libro
  const chapterMax = useMemo(
    () => CHAPTERS_BY_SLUG[bookSlug] ?? 1,
    [bookSlug]
  );
  const chapterOptions = useMemo(
    () => Array.from({ length: chapterMax }, (_, i) => i + 1),
    [chapterMax]
  );

  // Opciones de versículos (1..verseCount)
  const verseOptions = useMemo(
    () =>
      verseCount > 0
        ? Array.from({ length: verseCount }, (_, i) => i + 1)
        : [],
    [verseCount]
  );

  // Referencia visible (para copiar)
  const passageRef = useMemo(() => {
    const hasRange = vFrom && vTo && Number(vTo) >= Number(vFrom);
    return `${bookName} ${chapter}:${vFrom || ''}${hasRange && vTo ? '-' + vTo : ''
      }`
      .replace(/\s+/g, ' ')
      .trim();
  }, [bookName, chapter, vFrom, vTo]);

  // Traducción al nombre de libro en inglés que espera el API
  const engBook = useMemo(
    () => ENG_BY_SLUG[bookSlug] ?? 'John',
    [bookSlug]
  );

  // Carga/recarga de conteo de versículos
  const loadVerseCount = useCallback(async () => {
    if (!engBook || !chapter) return;
    setLoadingMeta(true);
    try {
      const count = await fetchChapterCount({
        bible: version,
        engBook,
        chapter: Number(chapter),
      });
      setVerseCount(count || 0);
      if (count > 0) {
        setVFrom(prev => Math.min(Math.max(1, prev || 1), count));
        setVTo(prev => {
          const safe = Math.min(Math.max(1, prev || 1), count);
          return safe < (vFrom || 1) ? (vFrom || 1) : safe;
        });
      } else {
        setVFrom(1);
        setVTo(1);
      }
    } catch {
      setVerseCount(0);
      setVFrom(1);
      setVTo(1);
    } finally {
      setLoadingMeta(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version, engBook, chapter, vFrom]);

  useEffect(() => {
    loadVerseCount();
  }, [version, bookSlug, chapter, loadVerseCount]);

  // Handlers
  const handleBookChange = useCallback(slug => {
    setBookSlug(slug);
    setChapter(1);
    setVFrom(1);
    setVTo(1);
    setResult(null);
    setError('');
  }, []);

  const handleChapterChange = useCallback(next => {
    const n = Number(next) || 1;
    setChapter(n);
    setVFrom(1);
    setVTo(1);
    setResult(null);
    setError('');
  }, []);

  const handleVFrom = useCallback(
    next => {
      const from = Math.max(
        1,
        Math.min(Number(next) || 1, verseCount || 1)
      );
      setVFrom(from);
      setVTo(prev =>
        Math.max(from, Math.min(prev || from, verseCount || from))
      );
    },
    [verseCount]
  );

  const handleVTo = useCallback(
    next => {
      const to = Math.max(
        1,
        Math.min(Number(next) || 1, verseCount || 1)
      );
      setVTo(Math.max(vFrom || 1, to));
    },
    [verseCount, vFrom]
  );

  // Buscar
  const onSearch = useCallback(async () => {
    if (!engBook) {
      toast({
        title: 'Libro no válido',
        description: 'Selecciona un libro válido.',
        status: 'warning',
      });
      return;
    }
    if (!chapter) {
      toast({
        title: 'Capítulo requerido',
        description: 'Elige un capítulo.',
        status: 'warning',
      });
      return;
    }
    if (!vFrom) {
      toast({
        title: 'Versículo inicial requerido',
        status: 'warning',
      });
      return;
    }

    setLoading(true);
    setError('');
    setResult(null);
    try {
      const data = await fetchPassage({
        bible: version,
        engBook,
        chapter: Number(chapter) || 1,
        vFrom: Number(vFrom) || 1,
        vTo: Number(vTo) || Number(vFrom) || 1,
      });
      setResult({
        reference: `${bookName} ${chapter}:${vFrom}${vTo && vTo !== vFrom ? `-${vTo}` : ''
          }`,
        version,
        verses: data.verses,
      });
      toast({
        title: 'Pasaje cargado',
        description: passageRef,
        status: 'success',
        duration: 1200,
      });
    } catch (e) {
      const msg = e?.message?.includes('HTTP')
        ? `No se pudo obtener el pasaje (${e.message}).`
        : 'No se pudo obtener el pasaje.';
      setError(msg);
      toast({ title: 'Error', description: msg, status: 'error' });
    } finally {
      setLoading(false);
    }
  }, [
    version,
    engBook,
    chapter,
    vFrom,
    vTo,
    bookName,
    toast,
    passageRef,
  ]);

  // Reset
  const clearAll = () => {
    setVersion('rv_1909');
    setBookSlug('juan');
    setChapter(3);
    setVFrom(16);
    setVTo(18);
    setResult(null);
    setError('');
  };

  // Copiar referencia
  const copyRef = async () => {
    try {
      await navigator.clipboard.writeText(passageRef);
      toast({ title: 'Referencia copiada', status: 'success' });
    } catch {
      toast({ title: 'No se pudo copiar', status: 'warning' });
    }
  };

  // Navegación capítulo anterior/siguiente
  const canPrev = chapter > 1;
  const canNext = chapter < (CHAPTERS_BY_SLUG[bookSlug] ?? 1);

  const goPrev = () => canPrev && handleChapterChange(chapter - 1);
  const goNext = () => canNext && handleChapterChange(chapter + 1);

  const versionLabel = useMemo(
    () => VERSIONS.find(v => v.value === version)?.label ?? String(version),
    [version]
  );

  const glassBg = useColorModeValue('rgba(255, 255, 255, 0.85)', 'rgba(26, 32, 44, 0.85)');

  /* ───────────────────── Render ───────────────────── */
  return (
    <Box bg="bg.default" flex="1" fontFamily="Inter, sans-serif">
      <Container maxW="container.xl" py={{ base: 4, md: 10 }}>
        <Heading textAlign="center" mb={6} color="text.default" display={{ base: 'none', md: 'block' }}>
          Biblia
        </Heading>

        {/* Toolbar Card (Sticky Glassmorphism) */}
        <Box
          bg={glassBg}
          backdropFilter="blur(10px)"
          p={{ base: 4, md: 6 }}
          borderRadius="2xl"
          boxShadow="md"
          position={{ base: 'relative', md: 'sticky' }}
          top={{ base: 'auto', md: '88px' }}
          zIndex={10}
          border="1px solid"
          borderColor="border.default"
        >
          <SimpleGrid
            columns={{ base: 1, lg: 5 }}
            spacing={4}
            alignItems="end"
          >
            {/* Versión */}
            <Box>
              <Text mb={1} color="text.muted" fontWeight="semibold">
                Versión
              </Text>
              <ScrollDropdown buttonText={versionLabel}>
                <MenuOptionGroup
                  type="radio"
                  value={String(version)}
                  onChange={val => setVersion(String(val))}
                >
                  {VERSIONS.map(v => (
                    <MenuItemOption key={v.value} value={v.value}>
                      {v.label}
                    </MenuItemOption>
                  ))}
                </MenuOptionGroup>
              </ScrollDropdown>
            </Box>

            {/* Libro */}
            <Box>
              <Text mb={1} color="text.muted" fontWeight="semibold">
                Libro
              </Text>
              <ScrollDropdown buttonText={bookName}>
                <MenuOptionGroup
                  title="Antiguo Testamento"
                  type="radio"
                  value={String(bookSlug)}
                  onChange={val => handleBookChange(String(val))}
                >
                  {booksOT.map(b => (
                    <MenuItemOption key={b.slug} value={b.slug}>
                      {b.name}
                    </MenuItemOption>
                  ))}
                </MenuOptionGroup>

                <MenuDivider />

                <MenuOptionGroup
                  title="Nuevo Testamento"
                  type="radio"
                  value={String(bookSlug)}
                  onChange={val => handleBookChange(String(val))}
                >
                  {booksNT.map(b => (
                    <MenuItemOption key={b.slug} value={b.slug}>
                      {b.name}
                    </MenuItemOption>
                  ))}
                </MenuOptionGroup>
              </ScrollDropdown>
            </Box>

            {/* Capítulo + nav */}
            <Box>
              <Text mb={1} color="text.muted" fontWeight="semibold">
                Capítulo
              </Text>
              <HStack>
                <Tooltip label="Anterior">
                  <IconButton
                    aria-label="Anterior"
                    icon={<ChevronLeftIcon />}
                    onClick={goPrev}
                    isDisabled={!canPrev || loadingMeta}
                    size="sm"
                    variant="ghost"
                  />
                </Tooltip>

                <Box flex="1">
                  <ScrollDropdown
                    buttonText={String(chapter)}
                    isDisabled={loadingMeta}
                  >
                    <MenuOptionGroup
                      type="radio"
                      value={String(chapter)}
                      onChange={val => handleChapterChange(String(val))}
                    >
                      {chapterOptions.map(n => (
                        <MenuItemOption key={n} value={String(n)}>
                          {n}
                        </MenuItemOption>
                      ))}
                    </MenuOptionGroup>
                  </ScrollDropdown>
                </Box>

                <Tooltip label="Siguiente">
                  <IconButton
                    aria-label="Siguiente"
                    icon={<ChevronRightIcon />}
                    onClick={goNext}
                    isDisabled={!canNext || loadingMeta}
                    size="sm"
                    variant="ghost"
                  />
                </Tooltip>
              </HStack>
            </Box>

            {/* Versículo desde */}
            <Box>
              <Text mb={1} color="text.muted" fontWeight="semibold">
                Versículo (desde)
              </Text>
              <ScrollDropdown
                buttonText={String(vFrom)}
                isDisabled={loadingMeta || verseOptions.length === 0}
              >
                <MenuOptionGroup
                  type="radio"
                  value={String(vFrom)}
                  onChange={val => handleVFrom(String(val))}
                >
                  {verseOptions.map(n => (
                    <MenuItemOption key={`vf-${n}`} value={String(n)}>
                      {n}
                    </MenuItemOption>
                  ))}
                </MenuOptionGroup>
              </ScrollDropdown>
            </Box>

            {/* Versículo hasta */}
            <Box>
              <Text mb={1} color="text.muted" fontWeight="semibold">
                Versículo (hasta)
              </Text>
              <ScrollDropdown
                buttonText={String(vTo)}
                isDisabled={loadingMeta || verseOptions.length === 0}
              >
                <MenuOptionGroup
                  type="radio"
                  value={String(vTo)}
                  onChange={val => handleVTo(String(val))}
                >
                  {verseOptions
                    .filter(n => n >= (vFrom || 1))
                    .map(n => (
                      <MenuItemOption key={`vt-${n}`} value={String(n)}>
                        {n}
                      </MenuItemOption>
                    ))}
                </MenuOptionGroup>
              </ScrollDropdown>
            </Box>
          </SimpleGrid>

          <HStack mt={4} spacing={3} flexWrap="wrap" align="center">
            <Button
              onClick={onSearch}
              colorScheme="blue"
              isLoading={loading}
              size="sm"
              w={{ base: '100%', sm: 'auto' }}
            >
              Buscar
            </Button>
            <Button
              onClick={clearAll}
              variant="outline"
              leftIcon={<RepeatIcon />}
              size="sm"
              w={{ base: '100%', sm: 'auto' }}
            >
              Limpiar
            </Button>
            <Tooltip label="Copiar referencia">
              <IconButton
                aria-label="Copiar referencia"
                icon={<CopyIcon />}
                onClick={copyRef}
                size="sm"
              />
            </Tooltip>
            <Tooltip label="Compartir imagen">
              <IconButton
                aria-label="Compartir imagen"
                icon={<FiShare2 />}
                onClick={() => setIsShareModalOpen(true)}
                isDisabled={!result || loading}
                size="sm"
              />
            </Tooltip>
            <Badge
              ml={{ base: 0, md: 'auto' }}
              mt={{ base: 2, md: 0 }}
              variant="subtle"
              colorScheme="blue"
            >
              {String(version).toUpperCase()}
            </Badge>
          </HStack>
        </Box>

        {/* Contenido (Modo Lectura) */}
        <Box
          mt={{ base: 6, md: 10 }}
          p={{ base: 2, md: 6 }}
          maxW="800px"
          mx="auto" // Centrado tipo libro
        >
          {/* Estado inicial */}
          {!result && !error && (
            <Text color="gray.600">
              Elige <b>libro</b>, <b>capítulo</b> y <b>versículos</b>,
              luego pulsa <b>Buscar</b>.
            </Text>
          )}

          {/* Error */}
          {error && (
            <Alert status="error" borderRadius="md" mb={4}>
              <AlertIcon />
              <Box>
                <Text fontWeight="semibold">
                  No se pudo obtener el pasaje.
                </Text>
                <Text fontSize="sm" color="text.muted" mt={1}>
                  {error}
                </Text>
              </Box>
            </Alert>
          )}

          {/* Resultado */}
          {result && (
            <VStack align="stretch" spacing={4}>
              <Flex justify="space-between" align="center" wrap="wrap" gap={2}>
                <Heading size="md" color="text.default">
                  {result.reference}
                </Heading>
                <HStack>
                  <Badge variant="solid" colorScheme="blue">
                    {String(result.version || '').toUpperCase()}
                  </Badge>
                </HStack>
              </Flex>

              <Divider />

              <VStack align="stretch" spacing={3}>
                {loading && (
                  <VStack align="stretch" spacing={2}>
                    <Skeleton height="5" />
                    <Skeleton height="5" />
                    <Skeleton height="5" />
                  </VStack>
                )}
                {!loading &&
                  result.verses.map((v, i) => (
                    <Text
                      key={`${v.verse}-${i}`}
                      color="text.default"
                      lineHeight="loose"
                      fontSize={{ base: 'lg', md: 'xl' }}
                      mb={2}
                      letterSpacing="normal"
                    >
                      <sup style={{ opacity: 0.6, marginRight: 6, fontSize: '0.6em' }}>
                        {v.verse}
                      </sup>
                      {String(v.text || '').trim()}
                    </Text>
                  ))}
              </VStack>
            </VStack>
          )}
        </Box>

        {/* Nota de fuente */}
        <Box mt={4}>
          <Text color="text.muted" fontSize="xs">
            Fuente: Bible SuperSearch API.
          </Text>
        </Box>
      </Container>
      
      <ShareCardModal 
        isOpen={isShareModalOpen} 
        onClose={() => setIsShareModalOpen(false)} 
        text={result?.verses?.map(v => v.text).join(' ')} 
        reference={result?.reference} 
      />
    </Box>
  );
}
